import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Passwort erfolgreich zurückgesetzt.");
      setTimeout(() => navigate("/login"), 2200);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] p-6">
        <div className="max-w-sm w-full text-center">
          <h2 className="font-display text-2xl uppercase mb-3">Ungültiger Link</h2>
          <p className="text-sm text-zinc-600 mb-6">
            Es wurde kein Reset-Token übergeben. Bitte fordere einen neuen Link an.
          </p>
          <Link to="/login" className="text-[#F97316] font-medium hover:underline">
            Zur Anmeldung →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] p-6">
      <div className="w-full max-w-sm animate-slide-up">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo-celentano.png" alt="Celentano" className="w-12 h-12 object-contain bg-black p-0.5" />
          <div>
            <div className="font-display text-base uppercase leading-none">Celentano</div>
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 mono mt-1">LAGERVERWALTUNG</div>
          </div>
        </div>

        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-3">02 · NEUES PASSWORT</div>
        <h2 className="font-display text-3xl uppercase mb-2">Zurücksetzen</h2>
        <p className="text-sm text-zinc-600 mb-8">
          Wähle ein neues Passwort für dein Konto.
        </p>

        {done ? (
          <div className="border-l-2 border-green-600 bg-green-50 px-4 py-4 text-sm text-green-800 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">Erledigt!</div>
              Du wirst gleich zur Anmeldung weitergeleitet…
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            <div>
              <Label htmlFor="new-password" className="text-xs uppercase tracking-wider font-semibold">
                Neues Passwort
              </Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="reset-password-input"
                className="mt-1.5 rounded-sm border-zinc-400 focus-visible:ring-[#F97316] focus-visible:ring-2 focus-visible:ring-offset-0"
                placeholder="Mind. 6 Zeichen"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-xs uppercase tracking-wider font-semibold">
                Passwort wiederholen
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="reset-confirm-input"
                className="mt-1.5 rounded-sm border-zinc-400 focus-visible:ring-[#F97316] focus-visible:ring-2 focus-visible:ring-offset-0"
                placeholder="Nochmal eingeben"
              />
            </div>

            {err && (
              <div data-testid="reset-error" className="border-l-2 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-700">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-button"
              className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-sm h-11 tracking-wide"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? "SPEICHERN…" : "PASSWORT SETZEN"}
            </Button>
          </form>
        )}

        <div className="mt-8 text-sm">
          <Link to="/login" className="text-[#F97316] font-medium hover:underline" data-testid="back-to-login">
            ← Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
}
